import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { setInspectionScheduledAt } from '@/api/orders'
import { Button } from '@/components/ui/Button'
import { formatMoscowDateTime, toDateTimeLocalInput } from '@/utils/format'

/**
 * «Осмотр: дата и время» в блоке «Данные» карточки заказа, рядом с адресом.
 * Ставит ОО/диспетчер, когда согласовали с клиентом — с создания заказа или в
 * окне назначения инспектора; пусто — время не согласовано. Первое заполнение
 * у заказа СберАвто отправляет Сберу событие «Назначен осмотр».
 */
export function OrderInspectionTime({
  orderId,
  value,
  canEdit,
}: {
  orderId: string
  value: string | null | undefined
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const mut = useMutation({
    mutationFn: (iso: string | null) => setInspectionScheduledAt(orderId, iso),
    onSuccess: () => {
      setEditing(false)
      void qc.invalidateQueries({ queryKey: ['order', orderId] })
    },
  })

  return (
    <div className="min-w-0">
      <div className="stryzh-input-label">{t('orderDetail.inspectionAt')}</div>
      {editing ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <input
            type="datetime-local"
            aria-label={t('orderDetail.inspectionAt')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="min-h-9 rounded-[var(--stryzh-radius-sm)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-2 text-[color:var(--stryzh-color-text-primary)] [color-scheme:dark]"
          />
          <Button
            type="button"
            variant="control"
            disabled={!draft || mut.isPending}
            onClick={() => mut.mutate(new Date(draft).toISOString())}
          >
            {t('common.save')}
          </Button>
          {value ? (
            <Button type="button" variant="ghost" disabled={mut.isPending} onClick={() => mut.mutate(null)}>
              {t('orderDetail.inspectionAtClear')}
            </Button>
          ) : null}
          <Button type="button" variant="ghost" disabled={mut.isPending} onClick={() => setEditing(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
          <span>{value ? formatMoscowDateTime(value) : t('orderDetail.inspectionAtEmpty')}</span>
          {canEdit ? (
            <button
              type="button"
              className="text-sm text-[color:var(--stryzh-color-accent-orange)] hover:underline"
              onClick={() => {
                setDraft(value ? toDateTimeLocalInput(value) : '')
                setEditing(true)
              }}
            >
              {value ? t('orderDetail.inspectionAtChange') : t('orderDetail.inspectionAtSet')}
            </button>
          ) : null}
        </div>
      )}
      {mut.isError ? <div className="mt-1 text-xs text-red-300">{(mut.error as Error).message}</div> : null}
    </div>
  )
}
